import { AuthUser } from './types';
import { PAGINAS_SISTEMA } from '@/types/usuarios';

const RAFAEL_EMAIL = 'rafael.p@prosperasistemas.com.br';

/** Opcional: se o e-mail no banco não for exatamente o esperado, defina o id do usuário Rafael no .env */
const RAFAEL_USER_ID_RAW = import.meta.env.VITE_RAFAEL_USER_ID;
const RAFAEL_USER_ID =
  RAFAEL_USER_ID_RAW !== undefined &&
  RAFAEL_USER_ID_RAW !== '' &&
  !Number.isNaN(Number(RAFAEL_USER_ID_RAW))
    ? Number(RAFAEL_USER_ID_RAW)
    : null;

export function normalizeEmail(email: string | null | undefined): string {
  return (email ?? '').trim().toLowerCase();
}

export function isRafaelEmail(email: string | null | undefined): boolean {
  return normalizeEmail(email) === RAFAEL_EMAIL;
}

/** Rafael: e-mail canônico OU (opcional) id em VITE_RAFAEL_USER_ID — garante menu/permissões nos testes. */
export function isRafaelUser(user: Pick<AuthUser, 'id' | 'email'> | null | undefined): boolean {
  if (!user) return false;
  if (isRafaelEmail(user.email)) return true;
  if (RAFAEL_USER_ID !== null && user.id === RAFAEL_USER_ID) return true;
  return false;
}

/** Linhas de usuarios_permissoes + usuário → mapa completo (evita sessão antiga sem chaves novas). */
export function mergePermissionsFromDb(
  usuario: Pick<AuthUser, 'email' | 'perfil_padrao' | 'id'>,
  permissoesRows: { pagina: string; acesso: boolean | null }[] | null | undefined
): Record<string, boolean> {
  if (isRafaelUser(usuario)) {
    return createPermissionsMap({
      id: usuario.id,
      nome: '',
      email: usuario.email ?? '',
      token: '',
      perfil_padrao: usuario.perfil_padrao,
      permissoes: {},
    });
  }

  const fromDb = (permissoesRows || []).reduce((acc, perm) => {
    acc[perm.pagina] = perm.acesso === true;
    return acc;
  }, {} as Record<string, boolean>);

  if (usuario.perfil_padrao?.toLowerCase() === 'admin') {
    const full = createPermissionsMap({
      id: 0,
      nome: '',
      email: usuario.email || 'x@x.com',
      token: '',
      perfil_padrao: 'admin',
      permissoes: {},
    });
    return { ...full, ...fromDb };
  }

  const base = PAGINAS_SISTEMA.reduce((acc, p) => {
    acc[p] = false;
    return acc;
  }, {} as Record<string, boolean>);

  return { ...base, ...fromDb };
}

export const createPermissionsMap = (usuario: AuthUser): Record<string, boolean> => {
  // Rafael sempre tem acesso total
  if (isRafaelUser(usuario)) {
    const todasPaginas = [
      'Dashboard',
      'Orçamentos',
      'CRM - Dashboard',
      'CRM - Leads',
      'CRM - Empresas',
      'CRM - Funil',
      'CRM - Reuniões',
      'CRM - Produtos',
      'CRM - Origens',
      'IA - Prompts',
      'RH - Funções',
      'RH - Vagas',
      'RH - Pipeline',
      'Prospecção Comercial',
      'Prospecção - Campanhas',
      'Prospecção - Leads',
      'Prospecção - Empresas',
      'Prospecção - Oportunidades',
      'Prospecção - Prompts IA',
      'Prospecção - Configurações',
      'Ordens de Serviço',
      'Chamados',
      'Clientes',
      'Funcionários',
      'Usuários',
      'Layout',
      'Análises',
      'Operação - TO DO',
      'Operação - Fechamento',
      'Análises - Rentabilidade Projetos',
      'Financeiro',
      'Análise Gerencial',
      'Relatórios',
      'Relatórios - Análise de Projetos',
      'Relatórios - Volume de Horas',
      'Relatórios - Resultados por Operador',
      'Relatórios - Análise Mensal OS',
      'Relatórios - Análise Mensal OS - Expandir Resumo Operador',
      'Tributação',
      'Tributação - NCM ICMS',
      'Tributação - Perfis',
      'Tributação - Regras Cálculo',
      'Tributação - Regras Financeiras',
      'Tributação - Tokens API',
      'Tributação - Base de Conhecimento',
      'Tributação - Confronto Fiscal',
      'Tributação - Perfil Fiscal',
      'Tributação - Diagnóstico Protheus',
      'Tributação - SPED',
      'Agenda Diversos',
      'Agendamento',
      'IA - Prompts',
    ];
    
    return todasPaginas.reduce((acc, pagina) => {
      acc[pagina] = true;
      return acc;
    }, {} as Record<string, boolean>);
  }

  // Administradores têm acesso total automático
  if (usuario.perfil_padrao?.toLowerCase() === 'admin') {
    const todasPaginas = [
      'Dashboard',
      'Orçamentos',
      'CRM - Dashboard',
      'CRM - Leads',
      'CRM - Empresas',
      'CRM - Funil',
      'CRM - Reuniões',
      'CRM - Produtos',
      'CRM - Origens',
      'RH - Funções',
      'RH - Vagas',
      'RH - Pipeline',
      'Prospecção Comercial',
      'Prospecção - Campanhas',
      'Prospecção - Leads',
      'Prospecção - Empresas',
      'Prospecção - Oportunidades',
      'Prospecção - Prompts IA',
      'Prospecção - Configurações',
      'Ordens de Serviço',
      'Chamados',
      'Clientes',
      'Funcionários',
      'Usuários',
      'Layout',
      'Análises',
      'Operação - TO DO',
      'Operação - Fechamento',
      'Análises - Rentabilidade Projetos',
      'Financeiro',
      'Análise Gerencial',
      'Relatórios',
      'Relatórios - Análise de Projetos',
      'Relatórios - Volume de Horas',
      'Relatórios - Resultados por Operador',
      'Relatórios - Análise Mensal OS',
      'Relatórios - Análise Mensal OS - Expandir Resumo Operador',
      'Tributação',
      'Tributação - NCM ICMS',
      'Tributação - Perfis',
      'Tributação - Regras Cálculo',
      'Tributação - Regras Financeiras',
      'Tributação - Tokens API',
      'Tributação - Base de Conhecimento',
      'Tributação - Confronto Fiscal',
      'Tributação - Perfil Fiscal',
      'Tributação - Diagnóstico Protheus',
      'Tributação - SPED',
      'Agenda Diversos',
      'Agendamento',
    ];
    
    return todasPaginas.reduce((acc, pagina) => {
      acc[pagina] = true;
      return acc;
    }, {} as Record<string, boolean>);
  }
  
  return {};
};

export const hasPermission = (user: AuthUser | null, pagina: string): boolean => {
  
  if (!user) {
    return false;
  }
  
  // Rafael sempre tem acesso total
  if (isRafaelUser(user)) {
    return true;
  }
  
  // Administradores têm acesso total automático
  if (user.perfil_padrao?.toLowerCase() === 'admin') {
    return true;
  }
  
  // Outros usuários devem respeitar permissões específicas
  if (pagina === 'Operação - TO DO') {
    return user.permissoes['Operação'] === true || user.permissoes[pagina] === true;
  }

  if (pagina === 'Operação - Fechamento') {
    return (
      user.permissoes[pagina] === true ||
      user.permissoes['Análises'] === true ||
      user.permissoes['Análise Gerencial'] === true
    );
  }

  if (pagina === 'Análises - Rentabilidade Projetos') {
    return user.permissoes['Análises'] === true || user.permissoes[pagina] === true;
  }

  if (pagina.startsWith('Prospecção - ')) {
    return user.permissoes['Prospecção Comercial'] === true || user.permissoes[pagina] === true;
  }

  if (pagina.startsWith('CRM - ')) {
    return user.permissoes[pagina] === true;
  }

  // Tributação: cada subtela respeita só a própria chave. A chave 'Tributação' não libera as demais —
  // antes liberava, e desmarcar uma subtela (ex.: Base de Conhecimento) não tirava o acesso.
  return user.permissoes[pagina] === true;
};
